import {
  switchMap,
  mergeMap,
  filter,
  mapTo,
  map,
  catchError,
  withLatestFrom
} from 'rxjs/operators'
import { of } from 'rxjs'
import Actions from '../actions'
import { getJSON, isLoggedIn, postJSON } from '../utils/fetchUtils'
import { catchAndDispatchCustomError } from '../utils/customErrorHandler'
import { getPayload, startPolling } from '../utils/rxUtils'
import { ofType } from 'redux-observable'
import config from '../config.json'
import { filterCanViewFullApp } from '../utils/accountPermissionsUtils'
import { getIsHmoMember } from '../utils/hmoUtils'
import { myMinorChildren } from '../utils/familyUtils'
import { HMOProviders } from '../consts'

export const loadProfile = action$ => {
  return action$.pipe(
    getPayload(Actions.IS_SIGNED_IN_RECEIVED),
    filter(({ isSignedIn }) => isSignedIn),
    mapTo(Actions.createAction(Actions.USER_PROFILE_REQUESTED))
  )
}

export const userProfile = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.USER_PROFILE_REQUESTED),
    withLatestFrom(state$),
    // get profile if user is logged in
    filter(isLoggedIn),
    switchMap(() => {
      return getJSON('/profile/').pipe(
        mergeMap(profile => {
          return of(
            Actions.createAction(Actions.USER_PROFILE_RECEIVED, { profile }),
            Actions.createAction(Actions.USER_PROFILE_EXTENDED_REQUESTED),
            Actions.createAction(Actions.APPOINTMENTS_POLL_REQUESTED),
            Actions.createAction(Actions.QUEST_REFERRALS_REQUESTED, {
              id: profile.id
            }),
            Actions.createAction(Actions.QUEST_REFERRALS_RESULTS_REQUESTED)
          )
        }),
        catchAndDispatchCustomError
      )
    })
  )

export const getAuthenticatedLinks = action$ =>
  action$.pipe(
    getPayload(Actions.FRONTEND_AUTHENTICATED_URLS_REQUESTED),
    switchMap(params => {
      return getJSON(
        `/get-frontend-authenticated-urls/${
          params?.year ? `?year=${params?.year}` : ''
        }`
      ).pipe(
        map(payload =>
          Actions.createAction(
            Actions.FRONTEND_AUTHENTICATED_URLS_RECEIVED,
            payload
          )
        ),
        catchAndDispatchCustomError
      )
    })
  )

export const getAccumelators = action$ =>
  action$.pipe(
    getPayload(Actions.MEMBERS_ACCUMULATORS_REQUESTED),
    switchMap(({ memberIds, year }) => {
      return postJSON(`/tpa/members-accumulators/`, { memberIds, year }).pipe(
        map(payload =>
          Actions.createAction(Actions.MEMBERS_ACCUMULATORS_RECEIVED, payload)
        ),
        catchError(error => {
          return of(
            Actions.createAction(Actions.MEMBERS_ACCUMULATORS_FAILED, { error })
          )
        })
      )
    })
  )

export const getCareTeamIfHmoMember = action$ =>
  action$.pipe(
    getPayload(Actions.USER_PROFILE_RECEIVED),
    filter(({ profile }) => getIsHmoMember(profile)),
    switchMap(({ profile }) => {
      // TODO: remove this check once all users are migrated to Antidote provider (January 2025)
      // maybe we will need to fetch from SHN something instead
      if (profile?.hmo_data?.hmo_provider === HMOProviders.NIRVANA)
        return of(
          Actions.createAction(Actions.PROVIDER_PCP_REQUESTED, {
            patientId: profile?.id
          })
        )
      else {
        return of(
          Actions.createAction(Actions.PROVIDER_PCP_REQUESTED, {
            patientId: profile?.id
          }),
          Actions.createAction(Actions.MEMBER_PLAN_DETAILS_REQUESTED)
        )
      }
    })
  )

export const loadAuthenticatedLinks = action$ => {
  return action$.pipe(
    getPayload(Actions.IS_SIGNED_IN_RECEIVED),
    filter(({ isSignedIn }) => isSignedIn),
    mapTo(Actions.createAction(Actions.FRONTEND_AUTHENTICATED_URLS_REQUESTED))
  )
}

export const initKidsPage = (actions$, state$) =>
  actions$.pipe(
    getPayload(Actions.USER_PROFILE_RECEIVED),
    withLatestFrom(state$),
    filter(([_, state]) => !filterCanViewFullApp(state)),
    map(() => Actions.createAction(Actions.INIT_KIDS_APP))
  )

export const notificationsPolling = action$ => {
  const takeUntil = action$.pipe(ofType(Actions.SIGNOUT_RECEIVED))
  return action$.pipe(
    getPayload(Actions.IS_SIGNED_IN_RECEIVED),
    filter(({ isSignedIn }) => isSignedIn),
    startPolling(config.pollingIntervalInSeconds, takeUntil),
    mergeMap(() => {
      return of(
        Actions.createAction(Actions.NOTIFICATIONS_REQUESTED),
        Actions.createAction(Actions.APPOINTMENTS_POLL_REQUESTED)
      )
    })
  )
}
